import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowRight, CalendarCheck, CheckCircle2, ClipboardList, FileText, FlaskConical, MessageCircle, Package, Repeat2, ShoppingBag, UploadCloud } from 'lucide-react'
import { apiGet } from '../lib/api'
import { mergeOrdersWithLocalLabs } from '../lib/labOrders'
import { fetchMsoOrders, subscribeMsoOrdersChanged } from '../lib/msoOrders'
import { C } from '../lib/tokens'
import { Card, Pill, Btn } from '../components/UI'
import { ActionCard, EmptyState, LoadingCard } from '../components/PortalUX'
import OrderProgressTracker from '../components/OrderProgressTracker'
import BodyKpiDashboard from '../components/BodyKpiDashboard'
import { patientHidesShopTab } from '../lib/portalAccess'
import { consultationBookingPath, postPurchaseConsultationForOrder } from '../lib/postPurchaseConsultation'

const LAB_RECHECK_DAYS = 90

function parseLocalDate(value) {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  const text = String(value).trim()
  const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (dateOnly) {
    const date = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    return Number.isNaN(date.getTime()) ? null : date
  }
  const date = new Date(text)
  return Number.isNaN(date.getTime()) ? null : date
}

function dateInputValue(value) {
  const date = parseLocalDate(value)
  if (!date) return null
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

function displayDate(value) {
  const date = parseLocalDate(value)
  if (!date) return 'Not scheduled'
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function normalizeCollectionDate(value) {
  const dateOnly = dateInputValue(value)
  if (!dateOnly) return null
  const date = parseLocalDate(dateOnly)
  const today = parseLocalDate(dateInputValue(new Date()))
  const oldest = new Date(today.getFullYear() - 10, today.getMonth(), today.getDate())
  if (date > today || date < oldest) return null
  return dateOnly
}

function normalizeDateParts(month, day, year) {
  const normalizedYear = Number(year) < 100
    ? Number(year) >= 70 ? 1900 + Number(year) : 2000 + Number(year)
    : Number(year)
  const date = new Date(normalizedYear, Number(month) - 1, Number(day))
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== normalizedYear ||
    date.getMonth() !== Number(month) - 1 ||
    date.getDate() !== Number(day)
  ) {
    return null
  }
  return normalizeCollectionDate(date)
}

function collectionDateFromText(value) {
  const text = String(value || '')
  if (!text) return null
  const iso = text.match(/\b(20\d{2})[-_/.](0?[1-9]|1[0-2])[-_/.](0?[1-9]|[12]\d|3[01])\b/)
  if (iso) return normalizeDateParts(iso[2], iso[3], iso[1])
  const monthDayYear = [...text.matchAll(/\b(0?[1-9]|1[0-2])[-_/.](0?[1-9]|[12]\d|3[01])[-_/.](\d{2}|\d{4})\b/g)]
  for (const match of monthDayYear) {
    const normalized = normalizeDateParts(match[1], match[2], match[3])
    if (normalized) return normalized
  }
  return null
}

function labDateStrings(lab = {}) {
  return [
    lab?.fileName,
    lab?.filename,
    lab?.lab,
    lab?.lab_company,
    lab?.title,
    lab?.display_name,
    lab?.displayName,
    lab?.metadata?.fileName,
    lab?.metadata?.filename,
    lab?.metadata?.originalName,
    lab?.metadata?.display_name,
    lab?.metadata?.displayName,
    lab?.document?.display_name,
    lab?.pdf_document?.display_name,
    lab?.requisition_document?.display_name,
    ...(Array.isArray(lab?.lab_results) ? lab.lab_results : []).flatMap(result => [
      result?.display_name,
      result?.document?.display_name,
    ]),
  ].filter(Boolean)
}

function labCollectionDate(lab = {}) {
  const explicit = [
    lab?.collected_at,
    lab?.collectedAt,
    lab?.collection_date,
    lab?.collectionDate,
    lab?.specimen_collected_at,
    lab?.specimenCollectedAt,
    lab?.sample_collected_at,
    lab?.sampleCollectedAt,
    lab?.draw_date,
    lab?.drawDate,
    lab?.metadata?.collected_at,
    lab?.metadata?.collectedAt,
    lab?.metadata?.collection_date,
    lab?.metadata?.collectionDate,
    lab?.metadata?.specimen_collected_at,
    lab?.metadata?.specimenCollectedAt,
    lab?.metadata?.sample_collected_at,
    lab?.metadata?.sampleCollectedAt,
    lab?.metadata?.draw_date,
    lab?.metadata?.drawDate,
    lab?.test_date,
  ].map(normalizeCollectionDate).find(Boolean)
  if (explicit) return explicit
  return labDateStrings(lab).map(collectionDateFromText).find(Boolean) || null
}

function labReferenceDate(lab = {}) {
  return labCollectionDate(lab)
}

function addDays(value, days) {
  const date = parseLocalDate(value)
  if (!date) return null
  date.setDate(date.getDate() + days)
  return dateInputValue(date)
}

function daysUntil(value) {
  const due = parseLocalDate(value)
  if (!due) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  due.setHours(0, 0, 0, 0)
  return Math.ceil((due.getTime() - today.getTime()) / 86400000)
}

function latestLabDate(labs = []) {
  return labs
    .map(labReferenceDate)
    .filter(Boolean)
    .sort((a, b) => (Date.parse(b) || 0) - (Date.parse(a) || 0))[0] || null
}

const INACTIVE_ORDER_STATUSES = new Set([
  'delivered',
  'order_delivered',
  'delivered_to_patient',
  'cancelled',
  'canceled',
  'order_cancelled',
  'order_canceled',
  'deleted',
  'order_deleted',
  'refunded',
  'provider_denied',
  'provider_rejected',
  'denied',
])

const ORDERS_TAB_ONLY_STATUSES = new Set([
  'cancelled',
  'canceled',
  'order_cancelled',
  'order_canceled',
  'deleted',
  'order_deleted',
])

function normalizedStatus(status) {
  return String(status || '').trim().toLowerCase().replace(/[\s-]+/g, '_')
}

function metadataObject(value = {}) {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

function isActiveOrder(order) {
  return !INACTIVE_ORDER_STATUSES.has(normalizedStatus(order?.status))
}

function isDashboardOrder(order) {
  return !ORDERS_TAB_ONLY_STATUSES.has(normalizedStatus(order?.status))
}

function dashboardRecentOrders(orders) {
  const dashboardOrders = orders.filter(isDashboardOrder)
  const activeOrders = dashboardOrders.filter(isActiveOrder)
  if (activeOrders.length > 3) return activeOrders

  const inactiveSlots = 3 - activeOrders.length
  const inactiveOrders = dashboardOrders.filter(order => !isActiveOrder(order)).slice(0, inactiveSlots)
  const selectedIds = new Set([...activeOrders, ...inactiveOrders].map(order => order.id))
  return dashboardOrders.filter(order => selectedIds.has(order.id))
}

function recommendationSourceLabel(recommendation = {}) {
  if (recommendation.sourceLabel) return recommendation.sourceLabel
  if (recommendation.sourceType === 'ai_lab_review') return 'AI lab review'
  if (recommendation.sourceType === 'coach') return 'Coach recommendation'
  return 'Provider recommendation'
}

function recommendationState(recommendation = {}) {
  if (recommendation.status === 'purchased') return { label: 'Purchased', color: 'green', copy: 'This recommendation has already been started.' }
  const category = String(recommendation.product?.category || '').toLowerCase()
  if (recommendation.recommendationType === 'lab_panel' || category === 'lab') {
    return {
      label: 'Lab recommendation',
      color: 'blue',
      copy: recommendation.discountLabel ? `Ready to purchase with ${recommendation.discountLabel}.` : 'Ready to purchase in Labs.',
    }
  }
  if (recommendation.providerApproved || recommendation.status === 'approved') {
    return { label: 'Provider approved', color: 'green', copy: 'Approved and ready to purchase.' }
  }
  return { label: 'Needs provider approval', color: 'orange', copy: 'Start the request so a provider can review medical necessity.' }
}

function isVisibleDashboardRecommendation(recommendation = {}) {
  return !['inactive', 'declined', 'cancelled', 'canceled', 'archived', 'deleted', 'purchased'].includes(normalizedStatus(recommendation.status))
}

function recommendationProductId(recommendation = {}) {
  return recommendation.product?.id || recommendation.productId || recommendation.product_id || ''
}

function recommendationStartPath(recommendation = {}) {
  const productId = recommendationProductId(recommendation)
  const labPanelId = recommendation.labPanel?.id ||
    recommendation.product?.labPanel?.id ||
    recommendation.product?.metadata?.lab_panel_id ||
    (String(productId).startsWith('lab:') ? String(productId).slice(4) : '')
  if (labPanelId) {
    const params = new URLSearchParams({ tab: 'order', panel: labPanelId, open: '1' })
    if (recommendation.id) params.set('recommendation', recommendation.id)
    return `/labs?${params.toString()}`
  }
  if (!productId) return '/shop'
  const params = new URLSearchParams({ product: productId, open: '1' })
  if (recommendation.id) params.set('recommendation', recommendation.id)
  return `/shop?${params.toString()}`
}

const NON_STARTED_ORDER_STATUSES = new Set([
  'cancelled',
  'canceled',
  'order_cancelled',
  'order_canceled',
  'deleted',
  'order_deleted',
  'refunded',
  'provider_denied',
  'provider_rejected',
  'denied',
])

function searchableOrderText(order = {}) {
  const metadata = metadataObject(order.metadata)
  return [
    order.source,
    order.source_type,
    order.order_type,
    metadata.order_type,
    order.product_type,
    metadata.product_type,
    order.type,
    order.category,
    metadata.category,
    metadata.product_category,
    order.product?.category,
    order.product?.product_type,
    order.product?.display_name,
    order.product?.medication_name,
    order.display_name,
    order.product_display_name,
    order.product_full_name,
    order.medication_name,
    order.name,
    order.title,
    metadata.checkout_type,
    metadata.product_name,
    metadata.productName,
    metadata.product_display_name,
    metadata.medication_name,
    metadata.panel_name,
    metadata.lab_panel_name,
  ].filter(Boolean).join(' ').toLowerCase()
}

function isStartedOrder(order = {}) {
  return !NON_STARTED_ORDER_STATUSES.has(normalizedStatus(order.status))
}

function isLabRelatedOrder(order = {}) {
  const text = searchableOrderText(order)
  const metadata = metadataObject(order.metadata)
  return order.source === 'local_lab_order' ||
    normalizedStatus(order.order_type) === 'lab_order' ||
    normalizedStatus(metadata.order_type) === 'lab_order' ||
    normalizedStatus(metadata.checkout_type) === 'lab_order' ||
    normalizedStatus(order.product?.category) === 'lab' ||
    Boolean(order.lab_panel?.id || metadata.panel_id || metadata.lab_panel_id || metadata.labPanelId) ||
    /(^|[\s_-])(lab|labs|biobox|blood)([\s_-]|$)/.test(text)
}

const COACH_LAB_ORDER_SOURCES = new Set([
  'sub_account_lab_order_form',
  'admin_access_labs_order_form',
  'workflow_kisrx_access_labs_order',
  'workflow_kisrx_lab_order',
  'workflow_kisrx_order_lab_panel',
])

const NON_CARE_PRODUCT_ORDER_TYPES = new Set([
  'portal',
  'portal_access',
  'visit',
  'sync_visit',
  'lab_order',
])

const CARE_PRODUCT_ORDER_TYPES = new Set([
  'initial',
  'refill',
  'subscription',
  'autoship',
  'order',
  'product_order',
  'product_request',
  'medication_order',
  'prescription_order',
])

const CARE_PRODUCT_CHECKOUT_TYPES = new Set([
  'one_time',
  'subscription',
  'autoship',
])

function orderProductReferenceValues(order = {}) {
  const metadata = metadataObject(order.metadata)
  const metadataProduct = metadataObject(metadata.product)
  return [
    order.product?.id,
    order.product?.sku,
    order.product_id,
    order.productId,
    order.product_sku,
    metadata.product_id,
    metadata.productId,
    metadata.mso_product_id,
    metadata.product_sku,
    metadata.sku,
    metadataProduct.id,
    metadataProduct.sku,
  ].filter(Boolean)
}

function recommendationProductReferenceValues(recommendation = {}) {
  const product = recommendation.product || {}
  const metadata = metadataObject(recommendation.metadata)
  return [
    product.id,
    product.sku,
    recommendation.productId,
    recommendation.product_id,
    metadata.product_id,
    metadata.productId,
    metadata.sku,
  ]
    .filter(Boolean)
    .filter(value => !String(value).startsWith('lab:'))
}

function normalizedProductRefs(values = []) {
  return new Set(values
    .map(value => String(value || '').trim().toLowerCase())
    .filter(Boolean))
}

function orderMatchesRecommendationProduct(order = {}, recommendation = {}) {
  if (recommendation.recommendationType === 'lab_panel') return false
  const recommendationRefs = normalizedProductRefs(recommendationProductReferenceValues(recommendation))
  if (!recommendationRefs.size) return false
  return orderProductReferenceValues(order)
    .map(value => String(value || '').trim().toLowerCase())
    .some(value => recommendationRefs.has(value))
}

function labPanelNameFromOrder(order = {}) {
  const metadata = metadataObject(order.metadata)
  return order.lab_panel?.name ||
    metadata.panel_name ||
    metadata.lab_panel_name ||
    order.vendor_panel_name ||
    order.product_display_name ||
    order.product_full_name ||
    order.product?.display_name ||
    order.product ||
    'lab panel'
}

function isCoachOrderedLabOrder(order = {}) {
  if (!isLabRelatedOrder(order)) return false
  const metadata = metadataObject(order.metadata)
  const source = normalizedStatus(metadata.source || order.source || order.source_type)
  return COACH_LAB_ORDER_SOURCES.has(source) ||
    metadata.coach_billed === true ||
    metadata.no_patient_payment_required === true ||
    metadata.patient_payment_required === false ||
    metadata.client_pays === false ||
    metadata.payment_type === 'coach_billed'
}

function isCareProductOrder(order = {}) {
  if (isLabRelatedOrder(order)) return false
  const metadata = metadataObject(order.metadata)
  const orderType = normalizedStatus(order.order_type || metadata.order_type)
  const category = normalizedStatus(order.product?.category || order.category || metadata.category || metadata.product_category)
  const productType = normalizedStatus(order.product_type || order.product?.product_type || metadata.product_type)
  const checkoutType = normalizedStatus(metadata.checkout_type || metadata.checkoutType || metadata.payment_type || metadata.paymentType)
  if (NON_CARE_PRODUCT_ORDER_TYPES.has(orderType)) return false
  if (['portal', 'portal_access', 'visit', 'lab'].includes(category)) return false
  if (['portal', 'portal_access', 'visit', 'lab'].includes(productType)) return false
  const productName = order.product?.display_name ||
    order.product_display_name ||
    order.product_full_name ||
    order.medication_name ||
    metadata.product_name ||
    metadata.productName ||
    metadata.product_display_name ||
    metadata.medication_name
  const paymentEvidence = Number(order.amount_charged_cents || order.pricing?.amount_cents || metadata.final_amount_cents || metadata.original_amount_cents || 0) > 0 ||
    Boolean(metadata.no_charge || metadata.paid_at || metadata.payment_collected_at || metadata.stripe_checkout_completed_at || order.stripe_payment_intent_id || metadata.stripe_payment_intent_id || order.stripe_subscription_id || metadata.stripe_subscription_id)
  return orderProductReferenceValues(order).length > 0 ||
    CARE_PRODUCT_ORDER_TYPES.has(orderType) ||
    CARE_PRODUCT_CHECKOUT_TYPES.has(checkoutType) ||
    Boolean(productName && paymentEvidence)
}

function FirstStepCard({ icon: Icon, eyebrow, title, copy, active, complete, statusLabel, actions }) {
  const label = statusLabel || (complete ? 'Started' : active ? 'Recommended' : 'Next')
  return (
    <div style={{
      border: `1px solid ${active ? 'rgba(0,191,109,0.28)' : C.border}`,
      borderRadius: 18,
      background: active ? 'linear-gradient(180deg, #F2FFF8 0%, #FFFFFF 100%)' : '#FFFFFF',
      padding: 16,
      display: 'grid',
      gap: 12,
      boxShadow: active ? '0 14px 34px rgba(0,191,109,0.08)' : 'none',
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '38px minmax(0, 1fr) auto', gap: 12, alignItems: 'start' }}>
        <span style={{
          width: 38,
          height: 38,
          borderRadius: 19,
          background: complete ? '#EAF8F1' : active ? '#E9FFF3' : C.surfaceAlt,
          color: complete || active ? C.green : C.textTertiary,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {complete ? <CheckCircle2 size={19} /> : <Icon size={19} />}
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11.5, fontWeight: 840, color: active ? C.greenDark : C.textSecondary, textTransform: 'uppercase', letterSpacing: '.04em' }}>
            {eyebrow}
          </div>
          <div style={{ fontSize: 15, fontWeight: 820, color: C.text, marginTop: 4, lineHeight: 1.25 }}>
            {title}
          </div>
          <div style={{ fontSize: 12.8, color: C.textSecondary, lineHeight: 1.45, marginTop: 5 }}>
            {copy}
          </div>
        </div>
        <Pill color={complete ? 'green' : active ? 'blue' : 'default'}>{label}</Pill>
      </div>
      {actions && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {actions}
        </div>
      )}
    </div>
  )
}

export default function MsoDashboard({ patient }) {
  const navigate = useNavigate()
  const [orders, setOrders] = useState([])
  const [visits, setVisits] = useState([])
  const [labs, setLabs] = useState([])
  const [recommendations, setRecommendations] = useState([])
  const [loading, setLoading] = useState(true)

  const refreshOrders = useCallback(async () => {
    try {
      const nextOrders = await fetchMsoOrders()
      setOrders(nextOrders)
    } catch {
      setOrders(current => mergeOrdersWithLocalLabs(current))
    }
  }, [])

  const refreshRecommendations = useCallback(async () => {
    try {
      const data = await apiGet('/api/mso/recommended-products')
      setRecommendations((data.recommendations || []).filter(isVisibleDashboardRecommendation))
    } catch {
      // Keep the current dashboard state if recommendations are temporarily unavailable.
    }
  }, [])

  useEffect(() => {
    let mounted = true
    const loadOrders = async () => {
      try {
        const nextOrders = await fetchMsoOrders()
        if (mounted) setOrders(nextOrders)
      } catch {
        if (mounted) setOrders(mergeOrdersWithLocalLabs([]))
      }
    }
    Promise.all([
      loadOrders(),
      apiGet('/api/mso/visits').then(data => setVisits(data.visits || [])).catch(() => {}),
      apiGet('/api/mso/labs').then(data => setLabs(data.labs || [])).catch(() => setLabs([])),
      apiGet('/api/mso/recommended-products').then(data => setRecommendations((data.recommendations || []).filter(isVisibleDashboardRecommendation))).catch(() => {}),
    ]).finally(() => {
      if (mounted) setLoading(false)
    })

    const unsubscribeOrders = subscribeMsoOrdersChanged(() => {
      refreshOrders()
    })
    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshOrders()
        refreshRecommendations()
      }
    }
    const refreshOnFocus = () => {
      refreshOrders()
      refreshRecommendations()
    }
    const refreshTimer = window.setInterval(() => {
      refreshOrders()
      refreshRecommendations()
    }, 30000)

    document.addEventListener('visibilitychange', refreshIfVisible)
    window.addEventListener('focus', refreshOnFocus)
    return () => {
      mounted = false
      unsubscribeOrders()
      document.removeEventListener('visibilitychange', refreshIfVisible)
      window.removeEventListener('focus', refreshOnFocus)
      window.clearInterval(refreshTimer)
    }
  }, [refreshOrders, refreshRecommendations])

  if (loading) return <LoadingCard label="Loading dashboard..." />

  const hideShopTab = patientHidesShopTab(patient)
  const recentOrders = dashboardRecentOrders(orders)
  const visibleRecommendations = hideShopTab ? [] : recommendations
    .filter(isVisibleDashboardRecommendation)
    .filter(recommendation => !orders.some(order =>
      isStartedOrder(order) &&
      isCareProductOrder(order) &&
      orderMatchesRecommendationProduct(order, recommendation)
    ))
  const latestLabAt = latestLabDate(labs)
  const coachOrderedLabOrder = orders.find(order => isStartedOrder(order) && isCoachOrderedLabOrder(order))
  const coachOrderedLabPanelName = coachOrderedLabOrder ? labPanelNameFromOrder(coachOrderedLabOrder) : ''
  const labsStarted = Boolean(latestLabAt) || labs.length > 0 || orders.some(order => isStartedOrder(order) && isLabRelatedOrder(order))
  const firstProductStarted = orders.some(order => isStartedOrder(order) && isCareProductOrder(order))
  const showLabStep = !labsStarted
  const showCoachOrderedLabCard = Boolean(coachOrderedLabOrder) && !showLabStep
  const showProductStep = !hideShopTab && !firstProductStarted
  const showFirstSteps = showLabStep || showProductStep || showCoachOrderedLabCard
  const nextLabsAt = addDays(latestLabAt, LAB_RECHECK_DAYS)
  const nextLabsDays = daysUntil(nextLabsAt)
  const labsDueSoon = Number.isFinite(nextLabsDays) && nextLabsDays <= 14
  const labsCountdownLabel = !latestLabAt
    ? coachOrderedLabOrder ? 'Coach ordered' : 'Start baseline'
    : nextLabsDays < 0
      ? `${Math.abs(nextLabsDays)} days overdue`
      : nextLabsDays === 0
        ? 'Due today'
        : `${nextLabsDays} days left`
  const labsCountdownCopy = latestLabAt
    ? `Last collected ${displayDate(latestLabAt)}. Next recommended ${displayDate(nextLabsAt)}.`
    : coachOrderedLabOrder
      ? `Your coach already ordered ${coachOrderedLabPanelName}. Watch Labs and Orders for collection instructions and status.`
      : 'Upload or order labs to start your 90-day cadence.'
  const pendingConsultationOrder = orders.find(order => Boolean(consultationBookingPath(order))) || null
  const pendingConsultation = postPurchaseConsultationForOrder(pendingConsultationOrder)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {pendingConsultationOrder && (
        <Card style={{
          padding: 18,
          borderColor: 'rgba(0,191,109,0.25)',
          background: 'linear-gradient(180deg, #F0FFF7 0%, #FFFFFF 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 14,
          flexWrap: 'wrap',
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.greenDark, fontSize: 15, fontWeight: 820 }}>
              <CalendarCheck size={18} />
              Book your {pendingConsultation?.appointmentTypeName || 'consultation'}
            </div>
            <div style={{ color: C.textSecondary, fontSize: 13, marginTop: 5 }}>
              Your order is received. Schedule the required provider visit to continue.
            </div>
          </div>
          <Link to={consultationBookingPath(pendingConsultationOrder)} style={{ textDecoration: 'none' }}>
            <Btn variant="green">Book consultation</Btn>
          </Link>
        </Card>
      )}

      {showFirstSteps && (
      <div data-tour-id="next-steps">
        <Card style={{
          padding: 18,
          display: 'grid',
          gap: 16,
          borderColor: 'rgba(0,191,109,0.18)',
          boxShadow: C.shadowMedium,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <ClipboardList size={17} color={C.green} />
                <span style={{ fontSize: 12, fontWeight: 860, color: C.greenDark, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  Recommended first steps
                </span>
              </div>
              <h2 style={{ margin: 0, fontSize: 18, lineHeight: 1.22, color: C.text, letterSpacing: 0 }}>
                {showCoachOrderedLabCard && showProductStep
                  ? 'Your coach ordered labs. Next, choose your first product.'
                  : showCoachOrderedLabCard
                    ? 'Your coach ordered labs for you.'
                    : showLabStep && showProductStep
                  ? 'Start with labs, then order your first product.'
                  : showLabStep
                    ? 'Upload or purchase labs.'
                    : 'Order your first product.'}
              </h2>
              <p style={{ margin: 0, color: C.textSecondary, fontSize: 13.5, lineHeight: 1.48, maxWidth: 780 }}>
                {showCoachOrderedLabCard
                  ? `KIS Rx has ${coachOrderedLabPanelName} on file as a coach-ordered lab panel. You do not need to purchase that panel in the portal.`
                  : showLabStep
                  ? 'If you have recent lab results, upload them. If not, purchase labs first so the provider has current values before product review.'
                  : 'Your lab step is started. Go to Shop to choose the first product or treatment request you want reviewed.'}
              </p>
            </div>
            {showLabStep ? <Pill color="orange">Labs first</Pill> : showProductStep ? <Pill color="blue">Ready for shop</Pill> : <Pill color="green">Labs ordered</Pill>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            {showCoachOrderedLabCard && (
            <FirstStepCard
              icon={FlaskConical}
              eyebrow="Labs"
              title="Coach ordered your labs"
              copy={`Your coach already ordered ${coachOrderedLabPanelName}. Watch for collection instructions and lab status updates.`}
              complete
              statusLabel="Already ordered"
              actions={(
                <>
                  <Link to="/labs" style={{ textDecoration: 'none' }}>
                    <Btn size="sm" variant="green"><FlaskConical size={15} />View labs</Btn>
                  </Link>
                  <Link to="/orders" style={{ textDecoration: 'none' }}>
                    <Btn size="sm" variant="secondary"><FileText size={15} />View order</Btn>
                  </Link>
                </>
              )}
            />
            )}
            {showLabStep && (
            <FirstStepCard
              icon={FlaskConical}
              eyebrow="Step 1"
              title="Upload or purchase labs"
              copy="Use a recent report if you have one, or buy the right lab panel from KIS Rx."
              active
              actions={(
                <>
                  <Link to="/labs?tab=upload" style={{ textDecoration: 'none' }}>
                    <Btn size="sm" variant="green"><UploadCloud size={15} />Upload labs</Btn>
                  </Link>
                  <Link to="/labs?tab=order" style={{ textDecoration: 'none' }}>
                    <Btn size="sm" variant="secondary"><FlaskConical size={15} />Purchase labs</Btn>
                  </Link>
                </>
              )}
            />
            )}
            {showProductStep && (
            <FirstStepCard
              icon={ShoppingBag}
              eyebrow="Step 2"
              title="Order your first product"
              copy={labsStarted
                ? 'Go to Shop to choose the first product or treatment request you want reviewed.'
                : 'After labs are uploaded or purchased, go to Shop and start your first product request.'}
              active={labsStarted}
              actions={(
                <Link to="/shop" style={{ textDecoration: 'none' }}>
                  <Btn size="sm" variant={labsStarted ? 'green' : 'secondary'}>Open shop <ArrowRight size={15} /></Btn>
                </Link>
              )}
            />
            )}
          </div>
        </Card>
      </div>
      )}

      {visibleRecommendations.length > 0 && (
        <Card style={{ padding: 0, overflow: 'hidden', borderColor: 'rgba(0,191,109,0.18)', boxShadow: C.shadowMedium }}>
          <div style={{
            padding: '18px 22px',
            borderBottom: `1px solid ${C.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 780, color: C.text }}>
                <ClipboardList size={17} color={C.green} />
                Recommended products
              </div>
              <div style={{ fontSize: 12.5, color: C.textSecondary, marginTop: 4 }}>
                From lab review, your provider, or your coaching team.
              </div>
            </div>
            <Link to="/shop" style={{ textDecoration: 'none' }}>
              <Btn size="sm">Open shop</Btn>
            </Link>
          </div>
          <div style={{ display: 'grid' }}>
            {visibleRecommendations.slice(0, 4).map((recommendation, index) => {
              const state = recommendationState(recommendation)
              const product = recommendation.product || {}
              const productName = product.display_name || product.medication_name || 'Recommended product'
              const productId = recommendationProductId(recommendation)
              const startPath = recommendationStartPath(recommendation)
              const canStart = Boolean(productId) && recommendation.status !== 'purchased'
              return (
                <div
                  key={recommendation.id}
                  style={{
                    padding: '16px 22px',
                    borderBottom: index === Math.min(visibleRecommendations.length, 4) - 1 ? 'none' : `1px solid ${C.border}`,
                    display: 'grid',
                    gridTemplateColumns: '38px minmax(0, 1fr) auto',
                    gap: 12,
                    alignItems: 'center',
                  }}
                >
                  <span style={{
                    width: 38,
                    height: 38,
                    borderRadius: 19,
                    background: recommendation.providerApproved ? 'linear-gradient(180deg, #EAFBF2 0%, #DFF8EC 100%)' : '#FFF8E8',
                    color: recommendation.providerApproved ? C.green : '#B7791F',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {recommendation.providerApproved ? <CheckCircle2 size={18} /> : <Package size={18} />}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{
                        fontSize: 14.5,
                        fontWeight: 760,
                        color: C.text,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {productName}
                      </div>
                      <Pill color={state.color}>{state.label}</Pill>
                    </div>
                    <div style={{ fontSize: 12.5, color: C.textSecondary, marginTop: 4, lineHeight: 1.45 }}>
                      {recommendationSourceLabel(recommendation)} - {state.copy}
                    </div>
                    {recommendation.notes && (
                      <div style={{ fontSize: 12.5, color: C.textSecondary, marginTop: 4, lineHeight: 1.45 }}>
                        {recommendation.notes}
                      </div>
                    )}
                  </div>
                  {canStart ? (
                    <Link to={startPath} style={{ textDecoration: 'none' }}>
                      <Btn size="sm" variant="green">
                        Get started
                      </Btn>
                    </Link>
                  ) : (
                    <Pill color="gray">{recommendation.status || 'Not available'}</Pill>
                  )}
                </div>
              )
            })}
          </div>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        <Link to="/labs?tab=order" style={{ textDecoration: 'none' }}>
          <Card className="portal-action-card portal-action-card-compact dashboard-next-labs-card" style={{
            padding: 14,
            minHeight: 82,
            borderRadius: 18,
            display: 'grid',
            gridTemplateColumns: '34px minmax(0, 1fr) auto',
            alignItems: 'center',
            gap: 12,
            boxShadow: C.shadowSoft,
            borderColor: labsDueSoon ? '#F6D5B8' : C.border,
            background: labsDueSoon ? 'linear-gradient(180deg, #FFF8F1 0%, #FFFFFF 100%)' : 'linear-gradient(180deg, #FFFFFF 0%, #FBFCFE 100%)',
          }}>
            <span style={{
              width: 34,
              height: 34,
              borderRadius: 17,
              background: labsDueSoon ? '#FFF3E8' : '#EAF8F1',
              color: labsDueSoon ? '#A6520B' : C.green,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <FlaskConical size={17} strokeWidth={2} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 7, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ fontSize: 14, fontWeight: 780, color: C.text, letterSpacing: 0 }}>Next labs</div>
                <Pill color={labsDueSoon ? 'orange' : latestLabAt ? 'green' : 'blue'}>{labsCountdownLabel}</Pill>
              </div>
              <div style={{ fontSize: 12.2, color: C.textSecondary, lineHeight: 1.35, marginTop: 3, overflowWrap: 'anywhere' }}>
                {labsCountdownCopy}
              </div>
            </div>
            <FileText size={16} color={C.textTertiary} />
          </Card>
        </Link>
        <ActionCard compact to="/visits" icon={CalendarCheck} title="Visits" copy={`${visits.length} visit${visits.length === 1 ? '' : 's'} on file.`} />
        <ActionCard compact to="/messages" icon={MessageCircle} title="Messages" copy="Talk with your care team." />
        <ActionCard compact to="/prescriptions" icon={Repeat2} title="Request refill" copy="Renew eligible prescriptions." />
      </div>

      <Card className="dashboard-recent-orders-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{
          padding: '18px 22px',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 780, color: C.text }}>Recent orders</div>
            <div style={{ fontSize: 12.5, color: C.textSecondary, marginTop: 4 }}>Latest care requests and product activity.</div>
          </div>
          <Link to="/orders" style={{ textDecoration: 'none' }}>
            <Btn size="sm">View all</Btn>
          </Link>
        </div>

        {orders.length === 0 ? (
          <div style={{ padding: 22 }}>
            <EmptyState
              icon={Package}
              title="No orders yet."
              copy={hideShopTab
                ? 'When your care team creates orders for you, they will appear here.'
                : 'When you start a product request or checkout, it will appear here.'}
              actions={hideShopTab ? null : (
                <Link to="/shop" style={{ textDecoration: 'none' }}>
                  <Btn variant="green">Open shop</Btn>
                </Link>
              )}
            />
          </div>
        ) : (
          <div style={{ display: 'grid' }}>
            {recentOrders.map((order, index) => (
              <div
                key={order.id}
                style={{
                  padding: '18px 22px',
                  borderBottom: index === recentOrders.length - 1 ? 'none' : `1px solid ${C.border}`,
                  display: 'grid',
                  gap: 14,
                }}
              >
                <OrderProgressTracker order={order} compact />
              </div>
            ))}
          </div>
        )}
      </Card>

      <BodyKpiDashboard labs={labs} onOrder={() => navigate('/labs?tab=order')} />
    </div>
  )
}
